import { useState, useEffect } from 'react'

import { formatMoney } from 'services/util'

import themeCss from './sass/theme.module.scss'
import css from './sass/bidmodal.module.scss'
import DonationSelector from '../auctions/DonationSelector'
import DonationGoal from '../auctions/DonationGoal'

const MAX_DONATION_TOTAL = 10000
const roundDonationAmount = (value) => Math.round(value * 100) / 100
const formatDonationInputValue = (value) => `${roundDonationAmount(value)}`
const clampDonationAmount = (value, maxAmount) => Math.max(0, Math.min(roundDonationAmount(value), maxAmount))

export default function ConfirmDonationModal( { showDonationModal, setShowDonationModal, auctionUser,
  submitFn, donationResponse, setDonationResponse, auctionDetail} ) {  

  const [donation, setDonation] = useState()
  const [customDonationAmount, setCustomDonationAmount] = useState('')
  const [donationInputSource, setDonationInputSource] = useState('selector')
  const [success, setSuccess] = useState(false)

  const [userDonationTotal, setUserDonationTotal] = useState(0)

  const remainingDonationAllowance = Math.max(roundDonationAmount(MAX_DONATION_TOTAL - userDonationTotal), 0)
  const nextDonationTotal = roundDonationAmount(userDonationTotal + (Number(donation) || 0))
  const isDonationOverLimit = userDonationTotal > MAX_DONATION_TOTAL || nextDonationTotal > MAX_DONATION_TOTAL
  const isConfirmDisabled = !(donation > 0) || isDonationOverLimit

  useEffect(() => {
    const myAuctions = auctionUser?.MyAuctions
    const currentAuction = myAuctions?.find(a => a.AuctionId === auctionDetail.AuctionId)
    setUserDonationTotal(currentAuction?.DonationTotal || 0)

  }, [auctionUser, auctionDetail])

  useEffect(() => {
    //if there is an error from bid response, set failed bid to true to show the front end notify updates
    // update the loaded bid to the new bid amount
    if (donationResponse?.error) {
      setDonationResponse(undefined)
    }
    //if modal is showing
    if (showDonationModal) {
      //check to see if the bid amount was successful, if so, so the success modal
      if (donationResponse?.UserDonationTotal > 0) setSuccess(true)
    }

  }, [donationResponse, setDonationResponse, showDonationModal])

  useEffect(() => {
    if (donationInputSource !== 'custom' || customDonationAmount === '') return

    const parsedValue = Number(customDonationAmount)

    if (Number.isNaN(parsedValue)) return

    const clampedValue = clampDonationAmount(parsedValue, remainingDonationAllowance)

    if (clampedValue === parsedValue) return

    setCustomDonationAmount(formatDonationInputValue(clampedValue))
    setDonation(clampedValue)
  }, [customDonationAmount, donationInputSource, remainingDonationAllowance])

  const handleDonationChange = (value) => {
    setDonationInputSource('selector')
    setDonation(value)
    setCustomDonationAmount(value === undefined || value === null ? '' : `${value}`)
  }

  const handleCustomDonationChange = (event) => {
    const { value } = event.target

    setDonationInputSource('custom')

    if (value === '') {
      setCustomDonationAmount('')
      setDonation(0)
      return
    }

    if (!/^\d+(\.\d{0,2})?$/.test(value)) return

    const parsedValue = Number(value)

    if (Number.isNaN(parsedValue) || parsedValue < 0) return

    const clampedValue = clampDonationAmount(parsedValue, remainingDonationAllowance)

    if (clampedValue !== parsedValue) {
      setCustomDonationAmount(formatDonationInputValue(clampedValue))
      setDonation(clampedValue)
      return
    }

    setCustomDonationAmount(value)
    setDonation(clampedValue)
  }

  const closeSuccessfulDonationModal = () => {
    if (success) {
      //reset donation response to undefined to return to original state
      setDonationResponse(undefined)
      //remove successful state of donation
      setSuccess(false)
      // hide the modal
      setShowDonationModal(false)
    }
  }

  if (success) {
    return (
      <div className={css['bid-modal-container']}>
      <div className={themeCss['modal-background']} onClick={ () =>  closeSuccessfulDonationModal() }></div>

      <div className={`${css['bid-modal-content']} ${themeCss['box-top-yellow-accent-wide']}`}>
        
        <p className={css['bid-modal-content-title']}>Thanks for your donation!</p>
        <p>Thanks for supporting {auctionDetail.FundName}</p>
        <p>With your donation of ${formatMoney(userDonationTotal)}</p>

        <div className={css['bid-modal-actions']} >
  
          <div onClick={ () => closeSuccessfulDonationModal() }>
            <p className={css['bid-modal-cancel']}>
              Continue
            </p>
          </div>
        </div>
      </div>
    </div>
    )
  }

  return (
    <div className={css['bid-modal-container']}>
      <div className={themeCss['modal-background']} onClick={() => { setShowDonationModal(false) }}></div>

      <div className={`${css['bid-modal-content']} ${themeCss['box-top-yellow-accent-wide']}`}>
        
        <p className={css['bid-modal-content-title']}>Confirm Donation</p>
        <p> {auctionDetail.FundName}</p>

        {/* <DonationGoal auction={auctionDetail} /> */}

        <p>Your current donation amount: ${formatMoney(userDonationTotal)}</p>

        {userDonationTotal > 9999 &&
          <p className={css['donation-max']}>
            You have reached the maximum donation threshold, if you would like to donate more, please contact the auction administrator.
          </p>}

        <DonationSelector setDonation={handleDonationChange} donation={donation} auctionDetail={auctionDetail} />

        <div className={css['donation-modal-custom-container']}>
          <label className={css['donation-modal-custom-label']} htmlFor="custom-donation-amount">
            <span>Custom Amount (USD) ($10K max donation)</span>
            {isDonationOverLimit && <span className={css['donation-modal-custom-error']}>Donation over limit</span>}
          </label>
          <input id="custom-donation-amount" type="number" inputMode="decimal" min="0" max={remainingDonationAllowance} step="1"
            value={customDonationAmount}
            onChange={handleCustomDonationChange}
          />
        </div>

        <p> New total after donation: ${formatMoney(nextDonationTotal)}</p>

        <div className={css['bid-modal-actions']} >
        {/*    <button className={`${themeCss['FilledBlue']} ${themeCss['button']}`} onClick={()=>console.log('click for donation')}> */}
             <button className={`${themeCss['FilledBlue']} ${themeCss['button']}`} disabled={isConfirmDisabled} onClick={() => {
               if (!isConfirmDisabled) submitFn(donation)
             }}>
              Confirm Donation
            </button>

          <div onClick={() => { setShowDonationModal(false) }}>
            <p className={css['bid-modal-cancel']}>
              Cancel Donation
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
